import React, { useEffect, useState, useMemo } from 'react';
import { Card, Grid } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import PropTypes from 'prop-types';
import ArgonTypography from 'components/ArgonTypography';
import ArgonSelect from 'components/ArgonSelect';
import { useNotifications } from 'context/NotificationContext';

function DeployEnvironment({ envs, selectedEnv, selected }) {
  const [value, setValue] = useState('');
  const [options, setOptions] = useState([]);
  const { addNotification } = useNotifications();

  // Memoize the options to prevent re-calculation on every render
  const envOptions = useMemo(() => {
    if (!envs || !Array.isArray(envs)) return [];
    return envs.map((org) => ({
      value: org.org_id, // Use org_id as the unique key
      label: org.name,
    }));
  }, [envs]);

  useEffect(() => {
    setOptions(envOptions);

    // If a `selected` org object is passed in, set the initial value of the dropdown
    if (selected?.org_id && envs.some((env) => env.org_id === selected.org_id)) {
      setValue(selected.org_id);
    } else {
      setValue(''); // Reset if no valid selection
    }
  }, [envs, selected, envOptions]);

  const handleEnvChange = (e) => {
    const newOrgId = e.target.value;
    setValue(newOrgId);

    // Find the full org object that corresponds to the selected org_id
    const selectedOrgObject = envs.find((env) => env.org_id === newOrgId);

    // Pass the entire object (or null if deselected) up to the parent
    selectedEnv(selectedOrgObject);

    if (newOrgId) {
      addNotification({
        title: ['Info', 'Environment Changed'],
        severity: 'info',
        details: `Target environment set to: ${selectedOrgObject?.name || newOrgId}`,
      });
    }
  };

  return (
    <ArgonBox py={3}>
      <ArgonBox mb={3}>
        <Card>
          <ArgonBox px={4} py={4}>
            <Grid container spacing={3} direction="column">
              <Grid item>
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <ArgonTypography sx={{ fontSize: '12pt' }} fontWeight="regular">
                    Select the target environment:
                  </ArgonTypography>
                  <ArgonSelect
                    placeholder="Select Target Org"
                    value={value}
                    onChange={handleEnvChange}
                    options={options}
                  />
                </ArgonBox>
              </Grid>
            </Grid>
          </ArgonBox>
        </Card>
      </ArgonBox>
    </ArgonBox>
  );
}

DeployEnvironment.propTypes = {
  envs: PropTypes.array.isRequired,
  selectedEnv: PropTypes.func.isRequired,
  selected: PropTypes.object, // The full org object
};

export default DeployEnvironment;
